import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Dialog, DialogContent, TextField, Typography } from '@mui/material';

import theme from '../../../theme';
import { PRIMARY_COLOR } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { postCandidateAgencyUpdate } from '../../../views/usersList/usersListReducer';
import { SearchableSelectDropdownFilterDeprecated as SearchableSelectDropdown } from '../../SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import { EXTERNAL_ID_VALIDATION } from '../constant';

const agencyIdTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: '28px',
    fontFamily: PRIMARY_FONT[400],
    '& input::placeholder': {
      color: PRIMARY_COLOR[60],
      fontFamily: PRIMARY_FONT[400],
      opacity: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: PRIMARY_COLOR[60],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: PRIMARY_COLOR[60],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: PRIMARY_COLOR[60],
    },
  },
};

const styles = theme?.components?.userCard;
const CandidateAgencySelectionModal = ({
  userAgency,
  userRecruiter,
  externalAgencyId,
  onClose,
  userId,
}) => {
  const [selectedAgencyId, setSelectedAgencyId] = useState(userAgency?.ID);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState(userRecruiter?.ID);
  const [newExternalId, setNewExternalId] = useState(externalAgencyId);

  useEffect(() => {
    // if a user changes the agency, clear the recruiter field
    if (selectedAgencyId !== userAgency?.ID) {
      setSelectedRecruiterId(null);
    }
  }, [selectedAgencyId, userAgency]);

  const dispatch = useDispatch();
  const onRecruiterAgencyChangeSubmit = () => {
    const changeRecruiterAgencyPayload = {
      id: userId,
      fields: {
        agencyId: selectedAgencyId,
        recruiterId: selectedRecruiterId || null,
        externalAgencyId: newExternalId || null,
      },
    };
    dispatch(postCandidateAgencyUpdate(changeRecruiterAgencyPayload));
  };

  const agencyAPICallback = {
    httpMethod: 'POST',
    route: 'agency/read',
    generateBody: (searchTerm) => ({
      filters: [{ operation: 'icontains', field: 'name', value: searchTerm }],
    }),
  };

  const agencyRecruiterAPICallback = {
    httpMethod: 'POST',
    route: 'v2/users/read',
    generateBody: (searchTerm) => ({
      filters: [
        { operation: 'icontains', field: 'name', value: searchTerm },
        { operation: 'equals', field: 'role', value: UserRole.RECRUITER },
        { operation: 'equalsID', field: 'recruiter.agency.ID', value: selectedAgencyId },
      ],
    }),
  };

  const hasAChange =
    selectedAgencyId !== userAgency?.ID ||
    selectedRecruiterId !== userRecruiter?.ID ||
    newExternalId !== externalAgencyId;

  const isExternalIdValid =
    !newExternalId ||
    (newExternalId.length >= EXTERNAL_ID_VALIDATION.MIN_LENGTH &&
      newExternalId.length <= EXTERNAL_ID_VALIDATION.MAX_LENGTH);
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '16px',
          padding: '30px 38px 30px 38px',
        },
      }}
    >
      <DialogContent>
        <Box display="flex" sx={{ flexDirection: 'column', gap: '16px' }}>
          <Box>
            <Box display="flex" sx={{ gap: '16px' }}>
              <Typography sx={styles.agencyChangeFieldTitle}>
                {LanguageConverterV2('userCard.agency.placeholder')}
              </Typography>
              <Typography sx={styles.agencyChangeFieldDescription}>
                {LanguageConverterV2('userCard.agency.fieldDescription')}
              </Typography>
            </Box>
            <SearchableSelectDropdown
              placeholder={LanguageConverterV2('userCard.agency.placeholder')}
              onValueChange={(agencyId) => {
                setSelectedAgencyId(agencyId);
              }}
              optionsAPICallback={agencyAPICallback}
              getOptionLabel={(option) => option.name}
              initialValue={selectedAgencyId ?? null}
              wrapperSx={{ width: '100%' }}
            />
          </Box>
          <Box>
            <Box display="flex" sx={{ gap: '16px' }}>
              <Typography sx={styles.agencyChangeFieldTitle}>
                {LanguageConverterV2('userCard.recruiter.placeholder')}
              </Typography>
              <Typography sx={styles.agencyChangeFieldDescription}>
                {LanguageConverterV2('userCard.recruiter.fieldDescription')}
              </Typography>
            </Box>
            <SearchableSelectDropdown
              disabled={!selectedAgencyId}
              placeholder={LanguageConverterV2('userCard.recruiter.placeholder')}
              onValueChange={(recruiterId) => {
                setSelectedRecruiterId(recruiterId);
              }}
              optionsAPICallback={agencyRecruiterAPICallback}
              getOptionLabel={(option) => option.name}
              initialValue={selectedRecruiterId ?? null}
              wrapperSx={{ width: '100%' }}
            />
          </Box>
          <Box>
            <Box display="flex" sx={{ gap: '16px' }}>
              <Typography sx={styles.agencyChangeFieldTitle}>
                {LanguageConverterV2('userCard.agency.externalId.placeholder')}
              </Typography>
              <Typography sx={styles.agencyChangeFieldDescription}>
                {LanguageConverterV2('userCard.agency.externalId.description')}
              </Typography>
            </Box>
            <TextField
              variant="outlined"
              data-testid="candidateExternalIdUpdateInputField"
              sx={agencyIdTextFieldSx}
              placeholder={LanguageConverterV2('userCard.agency.externalId.placeholder')}
              value={newExternalId}
              onChange={(event) => {
                setNewExternalId(event.target.value);
              }}
            />
          </Box>
        </Box>
        <Box sx={styles.agencyButtonContainer}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ ...styles.backButton, textTransform: 'none' }}
          >
            {LanguageConverterV2('buttonText.goBack')}
          </Button>
          <Button
            data-testid="candidateAgencyUpdateButton"
            variant="contained"
            sx={styles.agencyChangeButton}
            onClick={onRecruiterAgencyChangeSubmit}
            disabled={!userId || !selectedAgencyId || !hasAChange || !isExternalIdValid}
          >
            {LanguageConverterV2('button.update')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CandidateAgencySelectionModal.propTypes = {
  userAgency: PropTypes.shape({
    ID: PropTypes.string,
    name: PropTypes.string,
  }),
  userRecruiter: PropTypes.shape({
    ID: PropTypes.string,
    name: PropTypes.string,
  }),
  externalAgencyId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
};

export default CandidateAgencySelectionModal;
